import * as React from 'react'
import { isEmpty, isEqual } from 'lodash'
import * as LodashFP from 'lodash/fp'

import {
  Box,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  DialogActions,
  IconButton,
  Divider,
  Typography,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from '@design/pt-ui'
import {
  CheckCircle as CircleCheckedFilled,
  RadioButtonUnchecked as CircleUnchecked,
} from '@mui/icons-material'

import { Project } from '../../interfaces/entities/project'
import * as actions from '../../model/project/actions'

import Select from '../../components/Select'
import { translateWithUserLocale } from '../../utils/i18n'
import { FormatsetId, Formatset } from '../../interfaces/entities/formatset'
import { Creative } from '../../interfaces/entities/creative'

import { IProjectService } from '../../interfaces/services/projects'
import DebouncedTextField from '../../components/DebouncedTextField'
import { ICreativeService } from '../../interfaces/services/creatives'

import DI from '../../app/di'
import CreativeCard from '../../components/CreativeCard/CreativeCard'
import { Labeled } from '../../interfaces/base'
import Loader from '../../components/Loader'
import { toast } from 'react-toastify'
import { IStringAnyMap } from '../../types/map'

interface ICreativesImportProps {
  open: boolean
  activator?: any
  onCloseDialog: () => void
  onOpen?: () => void
  onClose?: () => void
  confirmClose?: boolean
  project: Project
  creative?: Creative
  formatsetId: FormatsetId
  formats: Labeled[]
  formatsetName: string
  formatsetPlatform: string
  formatsetData: IStringAnyMap
}

interface IState {
  dialogShown: boolean
  error: string | null
  chosenFormatsets: Formatset[]
  creatives: Creative[]
  chosenProject: Project | null
  selectedCreatives: Creative[]
  isImporting: boolean
  selectAllChecked: boolean
  options: {
    projects: Project[] | null
    formatsets: Formatset[] | null
    creatives: Creative[] | null
  }
  searchValue: string
}

function CreativesImport(props: ICreativesImportProps) {
  const defaultProject = {
    value: props.project.id,
    label: props.project.name,
  }

  const getInitialState: (props: ICreativesImportProps) => IState = (
    props: ICreativesImportProps,
  ) => {
    return {
      dialogShown: false,
      selectedCreatives: [],
      chosenFormatsets: [],
      chosenProject: props.project,
      creatives: [],
      error: null,
      isImporting: false,
      selectAllChecked: false,
      options: {
        projects: null,
        formatsets: null,
        creatives: null,
      },
      searchValue: '',
    }
  }

  const [state, setState] = React.useState<IState>(getInitialState(props))

  React.useEffect(() => {
    state.error !== null && toast.error(state.error)
  }, [state.error])

  React.useEffect(() => {
    const validCreatives = validatedCreatives(state.creatives, props.formats)
    const areStillAllSelected = isEqual(state.selectedCreatives, validCreatives)
    setState((state) => ({
      ...state,
      selectAllChecked: areStillAllSelected,
    }))
  }, [state.chosenFormatsets, state.selectedCreatives, state.creatives]) // eslint-disable-line react-hooks/exhaustive-deps

  const getProjectService = () => {
    return DI.get('ProjectService') as IProjectService
  }

  const getCreativeService = () => {
    return DI.get('CreativeService') as ICreativeService
  }

  const filterFormatsets: (fs: Formatset) => boolean = (fs) => {
    if (fs.type !== props.formatsetPlatform) {
      return false
    }

    if (props.formatsetPlatform === 'performance_advertising') {
      if (!arePaFormatsetPlacementsSame(props.formatsetData, fs.data)) {
        return false
      }
    }

    return true
  }

  const arePaFormatsetPlacementsSame = (fsData1: any, fsData2: any) => {
    let placement1 = LodashFP.getOr(
      null,
      ['placement', 'performance_advertising'],
      fsData1,
    )
    let placement2 = LodashFP.getOr(
      null,
      ['placement', 'performance_advertising'],
      fsData2,
    )
    placement1 = Array.isArray(placement1) ? placement1[0] : placement1
    placement2 = Array.isArray(placement2) ? placement2[0] : placement2

    return placement1 && placement2 && placement1 === placement2
  }

  const initialize = async () => {
    const projectsResponse = await getProjectService().getProjects({
      perPage: 9999,
    })
    if (projectsResponse.type === 'error') {
      setState((state) => ({
        ...state,
        error: 'Could not load projects.',
      }))
    } else {
      const projects = projectsResponse.payload
      setState((state) => ({
        ...state,
        options: {
          ...state.options,
          projects: projects,
          formatsets: props.project.formatsets.filter(filterFormatsets),
        },
      }))
    }
  }

  const handleOpen = () => {
    setState((state) => ({
      ...state,
      dialogShown: true,
    }))

    initialize()
  }

  const handleProjectChange = async (value: any) => {
    if (state.options.projects === null) {
      return
    }

    const projectId = value.value as string

    const project =
      state.options.projects.find((p) => p.id === projectId) || null

    if (project == null) {
      return
    }

    const formatsetsResponse = await getProjectService().getFormatsets({
      projectId: projectId,
      includes: 'creatives',
    })
    if (formatsetsResponse.type === 'error') {
      setState((state) => ({
        ...state,
        error: 'Could not load formatsets.',
      }))
    } else {
      const formatsets = formatsetsResponse.payload
      setState((state) => ({
        ...state,
        chosenProject: project,
        chosenFormatsets: [],
        selectedCreatives: [],
        creatives: [],
        searchValue: '',
        options: {
          ...state.options,
          creatives: [],
          formatsets: formatsets.filter(filterFormatsets),
        },
      }))
    }
  }

  const handleFormatsetsChange = (value: any, { action }: any) => {
    if (state.options.formatsets === null) {
      return
    }

    if (action === 'remove-value') {
      setState((state) => ({
        ...state,
        selectedCreatives: [],
      }))
    }

    const formatsetIds = value.map((value: any) => value.value) as string[]
    const formatsets = formatsetIds
      .map((formatsetId) => {
        return (
          (state.options.formatsets || []).find((x) => x.id === formatsetId) ||
          null
        )
      })
      .filter((x) => x != null) as Formatset[]

    const creatives = formatsets.reduce(
      (creatives: Creative[], chosenFS: Formatset) => [
        ...creatives,
        ...chosenFS.creatives,
      ],
      [],
    )

    setState((state) => ({
      ...state,
      chosenFormatsets: formatsets,
      creatives: creatives,
      searchValue: '',
      options: {
        ...state.options,
        creatives: creatives,
      },
    }))
  }

  const renderCreativeCard = (creatives: Creative[]) =>
    creatives.length > 0 &&
    creatives.map((creative) => (
      <span
        style={{ padding: 15 }}
        onClick={() => handleCreative(creative)}
        className="cp-creative-card-import"
        key={creative.id}>
        <CreativeCard
          state={
            state.selectedCreatives.findIndex((s) => s.id === creative.id) !==
            -1
              ? 'selected'
              : 'default'
          }
          navigateOnClick={false}
          creative={creative}
          linkActive={false}
        />
      </span>
    ))

  const render = () => {
    const formats = props.formats

    const creativesIn: Creative[] = validatedCreatives(state.creatives, formats)

    return (
      creativesIn.length > 0 && (
        <>
          {renderCreativeCard(creativesIn)}
          <FormControlLabel
            control={
              <Checkbox
                checked={state.selectAllChecked}
                sx={{ marginLeft: '5px' }}
                onChange={handleCheckboxChange}
                icon={<CircleUnchecked />}
                checkedIcon={<CircleCheckedFilled />}
              />
            }
            label={translateWithUserLocale(
              'creative.import_creative(s)_select_all',
            )}
          />
        </>
      )
    )
  }

  const handleSearchCreatives = (value: string) => {
    setState((prevState) => ({
      ...prevState,
      searchValue: value,
      creatives:
        prevState?.options?.creatives?.filter((c) =>
          c.name.toLowerCase().includes(value.toLowerCase()),
        ) || [],
    }))
  }

  const handleCreative = (creative: Creative) => {
    setState((prevState) => ({
      ...prevState,
      selectedCreatives:
        prevState.selectedCreatives.findIndex((s) => s.id === creative.id) !==
        -1
          ? prevState.selectedCreatives.filter((s) => s.id !== creative.id)
          : [...prevState.selectedCreatives, creative],
    }))
  }

  const handleImport = async () => {
    const formatsetId = props.formatsetId
    const selectedCreativesIds = state.selectedCreatives.map((sc) => sc.id)

    try {
      setState((state) => ({
        ...state,
        isImporting: true,
      }))

      const imported = await getCreativeService().importCreatives(
        formatsetId,
        selectedCreativesIds,
      )

      if (imported.type === 'error') {
        setState((state) => ({
          ...state,
          error: 'Error while importing creative',
          isImporting: false,
        }))
      } else {
        setState((state) => ({
          ...state,
          error: null,
          isImporting: false,
        }))
        toast.success(
          translateWithUserLocale('creative.import_creative(s)_toast_success'),
        )
      }
    } catch (e) {
      console.error(e)
      setState((state) => ({
        ...state,
        error: translateWithUserLocale('error.something_went_wrong_apologetic'),
        isImporting: false,
      }))
    }

    const store = DI.get('store')
    store.dispatch(actions.getProject(props.project.id))

    handleClose()
  }

  const handleClose = () => {
    const initial = getInitialState(props)

    if (props.confirmClose) {
      setState((state) => ({
        ...state,
        dialogShown: true,
      }))
    }
    if (
      (state.dialogShown && props.onClose) ||
      (props.onClose && !props.confirmClose)
    ) {
      setState(() => ({
        ...initial,
        dialogShown: false,
      }))
    }
  }

  const handleCheckboxChange = (e: any) => {
    const isChecked = e.target.checked
    const validCreatives = validatedCreatives(state.creatives, props.formats)
    setState((state) => ({
      ...state,
      selectAllChecked: !state.selectAllChecked,
      selectedCreatives: isChecked ? validCreatives : [],
    }))
  }

  const validatedCreatives = (
    creatives: Creative[],
    formats: Labeled[],
  ): Creative[] => {
    const isValidFormat = (creative: Creative) =>
      formats.map((f) => f.name).includes(creative.type.name)

    return creatives.filter((creative: Creative) => isValidFormat(creative))
  }

  const { activator, formats } = props

  return (
    <>
      {activator != null ? (
        <Box onClick={handleOpen} sx={{ display: 'inherit' }}>
          {activator}
        </Box>
      ) : null}

      <Dialog
        fullWidth={true}
        sx={{ minHeight: '50vh' }}
        disableRestoreFocus={true}
        open={state.dialogShown}
        onClose={handleClose}
        maxWidth="lg">
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <DialogTitle sx={{ margin: '12px 0' }}>
            {translateWithUserLocale('creative.import_creative(s)_title')}
          </DialogTitle>
          <DialogActions>
            <IconButton onClick={handleClose} sx={{ color: 'primary.main' }}>
              <Icon>close</Icon>
            </IconButton>
          </DialogActions>
        </Box>
        <Divider />
        {state.isImporting ? (
          <Loader
            variant="overlay"
            message={translateWithUserLocale('loaders.importing_creatives')}
          />
        ) : null}
        <DialogContent
          sx={{
            backgroundColor: 'custom.gray.background',
            paddingTop: '25px',
          }}>
          <Paper sx={{ marginBottom: '25px' }}>
            <div style={{ width: 600, padding: 35 }}>
              <Typography>
                {translateWithUserLocale('creative.import_creative(s)_project')}
              </Typography>
              <Select
                isLoading={state.options.projects ? false : true}
                defaultValue={defaultProject}
                onChange={handleProjectChange}
                options={(state.options.projects || []).map((project) => {
                  return {
                    value: project.id,
                    label: project.name,
                  }
                })}
                isClearable={false}
                id="cp-select-project-import"
              />
            </div>
            <div style={{ width: 600, padding: 35 }}>
              <Typography>
                {translateWithUserLocale('creative.import_formatset(s)')}
              </Typography>
              <Select
                isLoading={state.options.formatsets ? false : true}
                isMulti={true}
                isDisabled={false}
                onChange={handleFormatsetsChange}
                value={state.chosenFormatsets.map((f) => ({
                  value: f.id,
                  label: f.name,
                }))}
                options={(state.options.formatsets || []).map((formatset) => {
                  return {
                    value: formatset.id,
                    label: formatset.name,
                  }
                })}
                isClearable={true}
                id="cp-select-formatset-import"
              />
            </div>
            <div style={{ paddingLeft: 15, paddingRight: 15 }}>
              {state.chosenFormatsets.length > 0 && (
                <>
                  <div style={{ padding: 35 }}>
                    <Typography>
                      {!isEmpty(validatedCreatives(state.creatives, formats))
                        ? translateWithUserLocale(
                            'creative.import_creative(s)_subtitle',
                          )
                        : translateWithUserLocale(
                            'creative.import_creative(s)_no_creatives_subtitle',
                          )}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'end',
                      }}>
                      <Typography
                        sx={{ marginRight: '15px', marginBottom: '5px' }}>
                        Search creatives:
                      </Typography>

                      <DebouncedTextField
                        value={state.searchValue}
                        onChange={handleSearchCreatives}
                        text={{
                          type: 'search',
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon>search</Icon>
                              </InputAdornment>
                            ),
                          },
                          inputProps: {
                            style: {
                              border: 'none',
                              boxShadow: 'none',
                            },
                          },
                        }}
                      />
                    </Box>
                  </div>
                  <Grid container={true}>{render()}</Grid>
                </>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                paddingBottom: 25,
                marginRight: 35,
                marginTop: 35,
              }}>
              <Button
                onClick={handleImport}
                disabled={state.isImporting || !state.selectedCreatives.length}
                color="primary"
                variant="contained">
                {translateWithUserLocale('creative.import_creative(s)_button')}
              </Button>
            </div>
          </Paper>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CreativesImport
